import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { AllUserInput } from "@/app/Users/Components/AllUserInput";
import { OrganisationInput } from "@/app/Organisations/Components/OrganisationInput";
import { useCreateThread } from "@/app/Threads/hooks/useCreateThread";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import { Button } from "@/components/catalyst/button";
import { PlusIcon } from "@heroicons/react/24/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";

type CreateWorkOrderThreadFormValues = {
  managed_by_id: string | null;
  user_subscribers: { id: string }[];
};

const validation = z.object({
  managed_by_id: z.string().nullable(),
  user_subscribers: z
    .array(z.object({ id: z.string().min(1, "Required") }))
    .min(1, "Add at least one user"),
});

type CreateWorkOrderThreadDialogProps = {
  workOrderId: string;
};

export const CreateWorkOrderThreadDialog: DialogComponent<
  CreateWorkOrderThreadDialogProps,
  { id: string }
> = ({ workOrderId }) => {
  const { close, resolve } = useDialogState<{ id: string }>();
  const { mutateAsync } = useCreateThread();

  return (
    <BaseDialog
      title="New Thread"
      subtitle="Create a thread on a work order and choose its subscribers"
    >
      <GenericForm<CreateWorkOrderThreadFormValues>
        initialValues={{
          managed_by_id: null,
          user_subscribers: [],
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          try {
            const result = await mutateAsync({
              input: {
                threadable_type: "WORK_ORDER",
                threadable_id: workOrderId,
                managed_by_id: values.managed_by_id,
                user_ids: values.user_subscribers.map((s) => s.id),
              },
            });
            await close();
            resolve?.({ id: result.createThread.id });
          } catch {
            toast.error("Failed to create thread");
          }
        }}
      >
        <OrganisationInput
          name="managed_by_id"
          label="Managed by organisation"
        />
        <SubscribersFieldArray
          name="user_subscribers"
          heading="Users"
          addLabel="Add user"
          renderInput={(index) => (
            <AllUserInput
              name={`user_subscribers.${index}.id`}
              label={`User ${index + 1}`}
            />
          )}
        />
        <DialogSubmitButton text="Create Thread" />
      </GenericForm>
    </BaseDialog>
  );
};

function SubscribersFieldArray({
  name,
  heading,
  addLabel,
  renderInput,
}: {
  name: "user_subscribers";
  heading: string;
  addLabel: string;
  renderInput: (index: number) => React.ReactNode;
}) {
  const { values, errors, submitCount } =
    useFormikContext<CreateWorkOrderThreadFormValues>();
  const items = values[name] ?? [];
  const arrayError = errors[name];
  const showArrayError =
    submitCount > 0 && typeof arrayError === "string" ? arrayError : null;

  return (
    <FieldArray name={name}>
      {(fieldHelpers: FieldArrayRenderProps) => (
        <div className="col-span-full">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-800">{heading}</h2>
            <Button
              type="button"
              plain
              onClick={() => fieldHelpers.push({ id: null })}
            >
              <PlusIcon className="size-4" data-slot="icon" />
              {addLabel}
            </Button>
          </div>

          {showArrayError ? (
            <p className="mb-2 text-sm text-red-600">{showArrayError}</p>
          ) : null}

          {items.length > 0 ? (
            <div className="overflow-hidden rounded-xl bg-white">
              <ul className="divide-y divide-gray-100">
                {items.map((_, index) => (
                  <li key={index} className="flex items-center gap-2 p-3">
                    <div className="grow">{renderInput(index)}</div>
                    <button
                      type="button"
                      className="rounded p-1 text-gray-400 hover:text-red-500"
                      onClick={() => fieldHelpers.remove(index)}
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </FieldArray>
  );
}
